import { EventContextProvider } from "@codat/analytics";
import {
  Button,
  Col,
  Divider,
  Grid,
  Row,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import {
  useGetProductsQuery,
  useGetSettingDefinitions,
  useUpdateSettings,
} from "api/public";
import { ProductSetting } from "api/schema/productsSchema";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import Page from "components/Page";

import { DynamicSetting, FormData } from "./DynamicSetting";
import styles from "./ProductSettings.module.scss";

export const ProductSettings = () => {
  const { productKey } = useParams<{ productKey: string }>();
  const [{ data: fetchedProducts }] = useGetProductsQuery();
  const [{ data: settings }, refetchSettings] =
    useGetSettingDefinitions(productKey);
  const [, updateSettings] = useUpdateSettings(productKey);
  const { addToast } = useToast();

  const product = fetchedProducts?.filter((p) => p.key === productKey)[0];
  const hasClientVisibleSettings = settings?.some((x) => x.clientVisible);

  const [oldValues, setOldValues] = useState<FormData>({});
  const [values, setValues] = useState<FormData>({});

  const { handleSubmit, reset, register, getValues, setValue } =
    useForm<FormData>({
      mode: "onChange",
      defaultValues: async () =>
        refetchSettings().then((response) => {
          const data = response.data
            .filter((x) => x.clientVisible)
            .reduce((current, setting) => {
              return {
                ...current,
                [setting.key]: setting.value ?? setting.defaultValue,
              };
            }, {});
          setOldValues(data);
          setValues(data);
          return data;
        }),
    });

  useEffect(() => {
    if (product?.name) {
      document.title = `${product?.name} | Products | Settings | Codat Portal`;
    } else {
      document.title = `Products | Settings | Codat Portal`;
    }
  }, [product?.name]);

  useEffect(() => {
    if (settings) {
      refetchSettings();
    }
    //we only want this to run when the productKey changes, setting changes would cause infinite loop
    //eslint-disable-next-line
  }, [productKey]);

  const onSubmit = async (formData: FormData) => {
    try {
      await updateSettings({
        data: formData,
      });
      addToast({
        message: "Settings updated successfully",
        variant: "success",
      });
    } catch {
      addToast({ message: "Settings updated failed", variant: "error" });
    }
  };

  const onReset = () => {
    reset();
    setValues(oldValues);
  };

  const updateValues = (key: string, value: string | boolean | Date) => {
    setValues({
      ...values,
      [key]: value,
    });
    setValue(key, value);
  };

  return (
    <Page>
      <EventContextProvider name="Settings Overview">
        <div>
          <Typography as="h1" variant="heading_xl">
            {product?.name} settings
          </Typography>
          <Divider />
        </div>
        {product && (
          <Grid className={styles.settingsGrid}>
            {product.hasSettings && hasClientVisibleSettings && settings ? (
              <form
                onSubmit={handleSubmit(onSubmit)}
                data-testid="settings-form"
              >
                {settings
                  .filter((x) => x.clientVisible)
                  .sort((a, b) => a.order - b.order)
                  .reduce(
                    (
                      chunks: ProductSetting[][],
                      item: ProductSetting,
                      index
                    ) => {
                      const chunk = Math.floor(index / 2);
                      chunks[chunk] = ([] as ProductSetting[]).concat(
                        chunks[chunk] || [],
                        item
                      );
                      return chunks;
                    },
                    []
                  )
                  .map((row, index) => {
                    return (
                      <Row key={index}>
                        {row.map((setting) => (
                          <Col size={6} key={setting.id}>
                            <DynamicSetting
                              {...{
                                setting,
                                register,
                                values,
                                updateValues,
                                getValues,
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                    );
                  })}
                <Row>
                  <Col size={12}>
                    <div className={styles.buttonContainer}>
                      <Button
                        label="Reset"
                        color="interface"
                        variant="secondary"
                        onClick={() => onReset()}
                        type="button"
                        data-testid="reset-button"
                      />
                      <Button label="Save" data-testid="save-button" />
                    </div>
                  </Col>
                </Row>
              </form>
            ) : (
              <Typography as="p" data-testid="no-settings-message">
                No settings required
              </Typography>
            )}
          </Grid>
        )}
      </EventContextProvider>
    </Page>
  );
};

export default ProductSettings;
